import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';
import {
  FixedAsset,
  CreateAssetDto,
  AssetFilters,
  AssetSummary,
  AssetCategory,
  CategoryScope,
  CreateAssetCategoryDto,
  AssetVendor,
  CreateAssetVendorDto,
  AssetInvoice,
  CreateAssetInvoiceDto,
  AssetInvoiceFilters,
} from '../../types/asset';

function buildQuery(filters?: AssetFilters): string {
  const params = new URLSearchParams();
  if (filters?.categoryId) params.append('categoryId', filters.categoryId);
  if (filters?.vendorId) params.append('vendorId', filters.vendorId);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.search) params.append('search', filters.search);
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.append('dateTo', filters.dateTo);
  params.append('page', String(filters?.page || 1));
  params.append('limit', String(filters?.limit || 20));
  return params.toString();
}

function buildInvoiceQuery(filters?: AssetInvoiceFilters): string {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.vendorId) params.append('vendorId', filters.vendorId);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
  if (filters?.dateTo) params.append('dateTo', filters.dateTo);
  params.append('page', String(filters?.page || 1));
  params.append('limit', String(filters?.limit || 20));
  return params.toString();
}

export const assetService = {
  // ── Register ──────────────────────────────────────────────────────────
  getAssets: (filters?: AssetFilters): Promise<ApiResponse<FixedAsset[]> & { meta: PaginationMeta }> => {
    return get<FixedAsset[]>(`/assets?${buildQuery(filters)}`) as Promise<
      ApiResponse<FixedAsset[]> & { meta: PaginationMeta }
    >;
  },

  getSummary: (filters?: AssetFilters): Promise<ApiResponse<AssetSummary>> => {
    const params = new URLSearchParams();
    if (filters?.categoryId) params.append('categoryId', filters.categoryId);
    if (filters?.vendorId) params.append('vendorId', filters.vendorId);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    const qs = params.toString();
    return get<AssetSummary>(`/assets/reports/summary${qs ? `?${qs}` : ''}`);
  },

  getAsset: (id: string): Promise<ApiResponse<FixedAsset>> => {
    return get<FixedAsset>(`/assets/${id}`);
  },

  createAsset: (data: CreateAssetDto): Promise<ApiResponse<FixedAsset>> => {
    return post<FixedAsset>('/assets', data);
  },

  updateAsset: (id: string, data: Partial<CreateAssetDto>): Promise<ApiResponse<FixedAsset>> => {
    return patch<FixedAsset>(`/assets/${id}`, data);
  },

  deleteAsset: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/assets/${id}`);
  },

  // ── Categories ────────────────────────────────────────────────────────
  getCategories: (
    search?: string,
    activeOnly = false,
    scope?: CategoryScope,
  ): Promise<ApiResponse<AssetCategory[]>> => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (activeOnly) params.set('activeOnly', 'true');
    if (scope) params.set('scope', scope);
    const qs = params.toString();
    return get<AssetCategory[]>(`/assets/categories${qs ? `?${qs}` : ''}`);
  },

  getPaginatedCategories: (
    page = 1,
    limit = 20,
    search?: string,
    scope?: CategoryScope,
  ): Promise<ApiResponse<AssetCategory[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (scope) params.set('scope', scope);
    params.set('page', String(page));
    params.set('limit', String(limit));
    return get<AssetCategory[]>(`/assets/categories?${params.toString()}`) as Promise<
      ApiResponse<AssetCategory[]> & { meta: PaginationMeta }
    >;
  },

  createCategory: (data: CreateAssetCategoryDto): Promise<ApiResponse<AssetCategory>> => {
    return post<AssetCategory>('/assets/categories', data);
  },

  getNextCategoryCode: (): Promise<ApiResponse<{ code: string }>> => {
    return get<{ code: string }>('/assets/categories/next-code');
  },

  updateCategory: (id: string, data: Partial<CreateAssetCategoryDto>): Promise<ApiResponse<AssetCategory>> => {
    return patch<AssetCategory>(`/assets/categories/${id}`, data);
  },

  deleteCategory: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/assets/categories/${id}`);
  },

  // ── Vendors ───────────────────────────────────────────────────────────
  getVendors: (search?: string, activeOnly = false): Promise<ApiResponse<AssetVendor[]>> => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (activeOnly) params.set('activeOnly', 'true');
    const qs = params.toString();
    return get<AssetVendor[]>(`/assets/vendors${qs ? `?${qs}` : ''}`);
  },

  getVendor: (id: string): Promise<ApiResponse<AssetVendor>> => {
    return get<AssetVendor>(`/assets/vendors/${id}`);
  },

  getPaginatedVendors: (
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<ApiResponse<AssetVendor[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    params.set('page', String(page));
    params.set('limit', String(limit));
    return get<AssetVendor[]>(`/assets/vendors?${params.toString()}`) as Promise<
      ApiResponse<AssetVendor[]> & { meta: PaginationMeta }
    >;
  },

  createVendor: (data: CreateAssetVendorDto): Promise<ApiResponse<AssetVendor>> => {
    return post<AssetVendor>('/assets/vendors', data);
  },

  updateVendor: (id: string, data: Partial<CreateAssetVendorDto>): Promise<ApiResponse<AssetVendor>> => {
    return patch<AssetVendor>(`/assets/vendors/${id}`, data);
  },

  deleteVendor: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/assets/vendors/${id}`);
  },

  // ── Purchase invoices (multi-asset) ───────────────────────────────────
  getInvoices: (
    filters?: AssetInvoiceFilters,
  ): Promise<ApiResponse<AssetInvoice[]> & { meta: PaginationMeta }> => {
    return get<AssetInvoice[]>(`/assets/invoices?${buildInvoiceQuery(filters)}`) as Promise<
      ApiResponse<AssetInvoice[]> & { meta: PaginationMeta }
    >;
  },

  getInvoice: (id: string): Promise<ApiResponse<AssetInvoice>> => {
    return get<AssetInvoice>(`/assets/invoices/${id}`);
  },

  createInvoice: (data: CreateAssetInvoiceDto): Promise<ApiResponse<AssetInvoice>> => {
    return post<AssetInvoice>('/assets/invoices', data);
  },

  deleteInvoice: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/assets/invoices/${id}`);
  },
};
